/**
 * El ÚNICO sitio desde el que se exporta.
 *
 * Había tres controles repartidos por la misma pantalla —«Generar paquete», la
 * descarga de la hoja y «Exportar XLSX» del árbol—, y ninguno decía en qué se
 * diferenciaba de los otros. Tres botones que producen tres archivos distintos
 * obligan a probar cuál es cuál, y en un entregable que se firma eso no es una
 * molestia: es un riesgo de mandar el documento equivocado.
 *
 * Ahora es un botón y un panel con las opciones nombradas por lo que entregan.
 * Cada pantalla declara las secciones que le aplican; las que no, no aparecen.
 */

import { useState } from "react";
import { api } from "../api/client";
import type { ExportLevel } from "../api/types";
import { DownloadButton } from "./DownloadButton";
import type { Period } from "./period";

const ETIQUETAS: Record<ExportLevel, string> = {
  formal: "Formato oficial",
  accounts: "Oficial + cuentas",
  odoo: "Oficial + jerarquía Odoo",
  entries: "Completo + apuntes",
};

/** Un documento que se descarga tal cual, sin opciones. */
export interface ExportDocumento {
  /** Qué es, dicho en una línea: «Hoja oficial · 03 ESF». */
  label: string;
  note?: string;
  xlsx: string;
  pdf?: string;
}

/** El árbol de auditoría, que sí tiene opciones de profundidad. */
export interface ExportAuditoria {
  reportKey: string;
  period: Period;
  levels: ExportLevel[];
  /** Los apuntes exigen además `journal:read`. */
  mayReadJournal: boolean;
}

/** Un bloque de archivos sueltos, cada uno con su propio enlace. */
export interface ExportArchivos {
  label: string;
  note?: string;
  /** `note` por ARCHIVO, no solo por bloque: en el F-07 conviven anexos
   *  cotejados contra el manual del Ministerio con uno que no lo está, y la
   *  advertencia tiene que ir pegada al que la merece. Una nota de bloque la
   *  extendería a los cuatro que sí están verificados. */
  archivos: { label: string; href: string; note?: string }[];
}

interface Props {
  documento?: ExportDocumento;
  /** La vista de detalle abierta dentro del documento, si la hay. */
  detalle?: ExportDocumento;
  paquete?: ExportDocumento & { extra?: React.ReactNode };
  auditoria?: ExportAuditoria;
  /** Archivos que no son XLSX/PDF del mismo documento: los CSV del F-07. */
  archivos?: ExportArchivos;
}

export function ExportMenu({
  documento,
  detalle,
  paquete,
  auditoria,
  archivos,
}: Props) {
  const [abierto, setAbierto] = useState(false);
  const [nivel, setNivel] = useState<ExportLevel>("accounts");
  const [ceros, setCeros] = useState(false);

  if (!documento && !detalle && !paquete && !auditoria && !archivos) return null;
  const niveles = (auditoria?.levels ?? []).filter(
    (valor) => valor !== "entries" || auditoria?.mayReadJournal,
  );

  return (
    <div className="export">
      <button
        type="button"
        className="btn primary"
        aria-expanded={abierto}
        onClick={() => setAbierto(!abierto)}
      >
        Exportar ▾
      </button>
      {abierto && (
        <div className="export-panel">
          {documento && (
            <Bloque doc={documento} onDone={() => setAbierto(false)} />
          )}
          {detalle && <Bloque doc={detalle} onDone={() => setAbierto(false)} />}
          {paquete && (
            <Bloque doc={paquete} onDone={() => setAbierto(false)}>
              {paquete.extra}
            </Bloque>
          )}

          {archivos && archivos.archivos.length > 0 && (
            <section className="export-bloque">
              <h3>{archivos.label}</h3>
              {archivos.note && <p className="small muted">{archivos.note}</p>}
              <div className="export-acciones">
                {archivos.archivos.map((archivo) => (
                  <div className="export-archivo" key={archivo.href}>
                    <DownloadButton
                      href={archivo.href}
                      onSuccess={() => setAbierto(false)}
                    >
                      {archivo.label}
                    </DownloadButton>
                    {archivo.note && <p className="small reparo">{archivo.note}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {auditoria && niveles.length > 0 && (
            <section className="export-bloque">
              <h3>Auditoría por niveles</h3>
              <p className="small muted">
                Un XLSX aparte con el estado abierto hasta donde se pida. No es un
                documento oficial y las hojas del paquete no cambian.
              </p>
              {niveles.map((valor) => (
                <label className="check" key={valor}>
                  <input
                    type="radio"
                    name="nivel"
                    checked={nivel === valor}
                    onChange={() => setNivel(valor)}
                  />
                  {ETIQUETAS[valor]}
                </label>
              ))}
              <label className="check">
                <input
                  type="checkbox"
                  checked={ceros}
                  onChange={(event) => setCeros(event.target.checked)}
                />
                Incluir cuentas con saldo cero
              </label>
              <DownloadButton
                href={api.reportTreeExportUrl(
                  auditoria.reportKey,
                  nivel,
                  auditoria.period,
                  ceros,
                )}
                onSuccess={() => setAbierto(false)}
              >
                Descargar auditoría (XLSX)
              </DownloadButton>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Bloque({
  doc,
  onDone,
  children,
}: {
  doc: ExportDocumento;
  onDone: () => void;
  children?: React.ReactNode;
}) {
  return (
    <section className="export-bloque">
      <h3>{doc.label}</h3>
      {doc.note && <p className="small muted">{doc.note}</p>}
      {children}
      <div className="export-acciones">
        <DownloadButton variant="primary" href={doc.xlsx} onSuccess={onDone}>
          XLSX
        </DownloadButton>
        {doc.pdf && (
          <DownloadButton href={doc.pdf} onSuccess={onDone}>
            PDF
          </DownloadButton>
        )}
      </div>
    </section>
  );
}
